import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { citaStartDelete, startConsultarPenalizacionCita } from '../../../actions/citas';
import { SocketContext } from '../../../context/SocketContext';
import { anulaServicioDeCitaConVarios, servicioIdTramoActual } from '../../../helpers/citas';
import { ModalPenalizacionCita } from './ModalPenalizacionCita';

const usarSockets = process.env.REACT_SOCKETS;

const CONSULTA_CARGANDO = 'cargando';
const CONSULTA_ERROR = 'error';
const CONSULTA_RESUELTA = 'resuelta';
const CONSULTA_INICIAL = { estado: CONSULTA_CARGANDO, penalizacion: null, msg: '' };
const MSG_SIN_CITA = 'No hay ninguna cita seleccionada.';

/**
 * Antes de ofrecer el borrado consulta `GET citas/:id/penalizacion` con el mismo servicio y estado
 * que usará el borrado. Con venta activa no deja borrar; en periodo de penalización pregunta si se
 * penaliza; fuera de él pide la confirmación de siempre. Si la consulta falla tampoco deja borrar:
 * borrar sin `penalizar` fuera de plazo penaliza por defecto sin haber preguntado.
 */
export const ModalDeleteCita = ({ activeModalDeleteCita, setActiveModalDeleteCita, estadoCita, setEstadoCita }) => {
    const dispatch = useDispatch();
    const { active: citaActive } = useSelector(state => state.citas);
    const { uid, empresa } = useSelector(state => state.auth);
    const { colores } = useSelector(state => state.ui);
    const { socket } = useContext( SocketContext );

    const [consulta, setConsulta] = useState(CONSULTA_INICIAL);
    const [intentoConsulta, setIntentoConsulta] = useState(0);

    const citaId = citaActive?._id;
    const servicioId = servicioIdTramoActual(citaActive);
    const estadoCitaNormalizado = estadoCita ?? '';

    useEffect(() => {
        if (!activeModalDeleteCita) {
            setConsulta(CONSULTA_INICIAL);
            return undefined;
        }
        if (!citaId) {
            setConsulta({ estado: CONSULTA_ERROR, penalizacion: null, msg: MSG_SIN_CITA });
            return undefined;
        }

        let respuestaObsoleta = false;
        setConsulta(CONSULTA_INICIAL);
        dispatch(startConsultarPenalizacionCita(citaId, { servicioId, estadoCita: estadoCitaNormalizado }))
            .then((respuesta) => {
                if (respuestaObsoleta) return;
                if (!respuesta?.ok) {
                    setConsulta({ estado: CONSULTA_ERROR, penalizacion: null, msg: respuesta?.msg || '' });
                    return;
                }
                setConsulta({ estado: CONSULTA_RESUELTA, penalizacion: respuesta, msg: '' });
            });
        return () => { respuestaObsoleta = true; };
    }, [activeModalDeleteCita, citaId, servicioId, estadoCitaNormalizado, intentoConsulta, dispatch]);

    const closeModal = () => {
        setActiveModalDeleteCita( false );
        setEstadoCita('');
    };

    const deleteCita = (penalizar) => {
        if (!citaId) return;

        if (usarSockets) {
            socket?.emit('eliminar-cita', { uid, citaId, servicioId, estadoCita: estadoCitaNormalizado, penalizar });
        } else {
            dispatch(citaStartDelete(citaId, empresa, estadoCitaNormalizado, servicioId, penalizar));
        }
        closeModal();
    };

    const reintentarConsulta = () => setIntentoConsulta(intento => intento + 1);

    const renderContenido = () => {
        const { estado, penalizacion, msg } = consulta;
        if (estado === CONSULTA_CARGANDO) {
            return <ContenidoCargando colores={colores} onCerrar={closeModal} />;
        }
        if (estado === CONSULTA_ERROR) {
            return <ContenidoError colores={colores} msg={msg} onReintentar={reintentarConsulta} onCerrar={closeModal} />;
        }
        if (penalizacion.tieneVentaActiva) {
            return <ContenidoVentaActiva colores={colores} onCerrar={closeModal} />;
        }
        if (penalizacion.enPeriodoPenalizacion) {
            return (
                <ModalPenalizacionCita
                    penalizacion={penalizacion}
                    anulaServicio={anulaServicioDeCitaConVarios(citaActive, estadoCitaNormalizado)}
                    onPenalizar={() => deleteCita(true)}
                    onSoloCancelar={() => deleteCita(false)}
                    onCerrar={closeModal}
                />
            );
        }
        return <ContenidoConfirmacion colores={colores} onConfirmar={() => deleteCita()} onCerrar={closeModal} />;
    };

    return (
        <div className={`modal-gestion ${(activeModalDeleteCita) ? 'active' : ''}`}>
            <div className="modal-bg"></div>
            <div className="modal-wrapper">
                {activeModalDeleteCita && renderContenido()}
                <div
                    className="modal-close"
                    onClick={ closeModal }
                ><span></span></div>
            </div>
        </div>
    )
}

const TituloModal = ({ colores, children }) => (
    <div className="modal-title">
        <h3 style={{ color: colores.colorSecundario }}>{children}</h3>
    </div>
);

const ContenidoCargando = ({ colores, onCerrar }) => (
    <>
        <TituloModal colores={colores}>Eliminar citas</TituloModal>
        <div className="modal-body">
            <p>Comprobando si la cancelación tiene penalización...</p>
        </div>
        <div className="modal-footer">
            <div className="buttons">
                <div className="btn btn-cancel" onClick={onCerrar}>Cerrar</div>
            </div>
        </div>
    </>
);

const ContenidoError = ({ colores, msg, onReintentar, onCerrar }) => (
    <>
        <TituloModal colores={colores}>Eliminar citas</TituloModal>
        <div className="modal-body">
            <p className="modal-gestion__aviso modal-gestion__aviso--error">
                No se ha podido comprobar si la cancelación tiene penalización, así que todavía no se
                puede eliminar la cita.
            </p>
            {msg && <p>{msg}</p>}
        </div>
        <div className="modal-footer">
            <div className="buttons">
                <div
                    className="btn btn-confirm"
                    style={{ backgroundColor: colores.colorPrincipal }}
                    onClick={onReintentar}
                >Reintentar</div>
                <div className="btn btn-cancel" onClick={onCerrar}>Cerrar</div>
            </div>
        </div>
    </>
);

const ContenidoVentaActiva = ({ colores, onCerrar }) => (
    <>
        <TituloModal colores={colores}>No se puede eliminar la cita</TituloModal>
        <div className="modal-body">
            <p>
                Esta cita tiene una venta activa, así que no se puede cancelar, anular uno de sus servicios
                ni marcar como no presentada.
            </p>
            <p>Primero devuelve la venta desde Facturación y después vuelve a intentarlo.</p>
        </div>
        <div className="modal-footer">
            <div className="buttons">
                <div className="btn btn-cancel" onClick={onCerrar}>Cerrar</div>
            </div>
        </div>
    </>
);

const ContenidoConfirmacion = ({ colores, onConfirmar, onCerrar }) => (
    <>
        <TituloModal colores={colores}>Eliminar citas</TituloModal>
        <div className="modal-body">
            <p>Tu cliente recibirá una notificación informándole de que su reserva no ha podido ser aceptada.<br />¿Estas seguro de confirmar esta acción?</p>
        </div>
        <div className="modal-footer">
            <div className="buttons">
                <div
                    className="btn btn-confirm"
                    style={{ backgroundColor: colores.colorPrincipal }}
                    onClick={onConfirmar}
                >Confirmar</div>
                <div className="btn btn-cancel" onClick={onCerrar}>Cancelar</div>
            </div>
        </div>
    </>
);
